import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { emittedRetentionApi } from "./axios";
import {
  DataI,
  ServiceResponseI,
  RetencionByReceivedI,
  EmitirRetencionPayloadI,
  EmittedRetentionI,
  ReconcileRetencionResultI,
} from "../../interfaces";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";

const handleErr = (err: unknown): never => {
  const obj = (err as AxiosError).response;
  if (obj?.status === 401) LogoutSystem();
  throw new Error((obj?.data as Error)?.message ?? "Error");
};

// ¿La factura recibida ya tiene retención? (se llama al click de "Retener")
export const getRetencionByReceived = async (
  receivedIdEncrypted: string,
): Promise<RetencionByReceivedI> => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await emittedRetentionApi.get<
      ServiceResponseI<RetencionByReceivedI>
    >(`/by-received/${receivedIdEncrypted}`, {
      headers: { Authorization: token },
    });
    return data.data!;
  } catch (err) {
    return handleErr(err);
  }
};

export const emitirRetencion = async (payload: EmitirRetencionPayloadI) => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await emittedRetentionApi.post("", payload, {
      headers: { Authorization: token },
      timeout: 120_000, // emisión SRI puede tardar
    });
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

// Reintenta una retención en ERROR/RECHAZADO/DEVUELTA (reusa la misma fila).
export const reintentarRetencion = async (idEncrypted: string) => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await emittedRetentionApi.post(
      `/${idEncrypted}/reintentar`,
      {},
      { headers: { Authorization: token }, timeout: 120_000 },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

// "Corregir retenciones": completa las que quedaron sin clave de acceso
// buscándolas en Magno por referenciaExterna ("RET-<id>").
export const reconcileEmittedRetenciones = async (): Promise<
  ServiceResponseI<ReconcileRetencionResultI>
> => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await emittedRetentionApi.post(
      "/reconcile",
      {},
      { headers: { Authorization: token }, timeout: 120_000 },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

// Soft-delete de una retención no autorizada.
export const eliminarRetencion = async (idEncrypted: string) => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await emittedRetentionApi.delete(`/${idEncrypted}`, {
      headers: { Authorization: token },
    });
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

// XML autorizado del comprobante de retención.
export const getEmittedRetentionXml = async (
  idEncrypted: string,
): Promise<Blob> => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await emittedRetentionApi.get(`/${idEncrypted}/xml`, {
      headers: { Authorization: token },
      responseType: "blob",
    });
    return data as Blob;
  } catch (err) {
    return handleErr(err);
  }
};

// RIDE (PDF) del comprobante de retención.
export const getEmittedRetentionPdf = async (
  idEncrypted: string,
): Promise<Blob> => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await emittedRetentionApi.get(`/${idEncrypted}/pdf`, {
      headers: { Authorization: token },
      responseType: "blob",
    });
    return data as Blob;
  } catch (err) {
    return handleErr(err);
  }
};

const getEmittedRetentions = async (params: {
  page: number;
  limit: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}): Promise<ServiceResponseI<DataI<EmittedRetentionI>>> => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await emittedRetentionApi.get<
      ServiceResponseI<DataI<EmittedRetentionI>>
    >("", {
      params,
      headers: { Authorization: token },
    });
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const useEmittedRetentions = (params: {
  page: number;
  limit: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}) => {
  const queryClient = useQueryClient();

  const emittedRetentionsQuery = useQuery(
    ["emitted-retentions", params],
    () => getEmittedRetentions(params),
    { keepPreviousData: true, refetchOnWindowFocus: false },
  );

  const invalidate = () => {
    queryClient.invalidateQueries(["emitted-retentions"]);
    // también refrescar el listado de facturas recibidas (para el flag)
    queryClient.invalidateQueries(["received-documents"]);
  };

  const emitirMutation = useMutation(
    (payload: EmitirRetencionPayloadI) => emitirRetencion(payload),
    { onSuccess: invalidate },
  );

  const reintentarMutation = useMutation(
    (idEncrypted: string) => reintentarRetencion(idEncrypted),
    { onSuccess: invalidate },
  );

  const eliminarMutation = useMutation(
    (idEncrypted: string) => eliminarRetencion(idEncrypted),
    { onSuccess: invalidate },
  );

  const reconcileMutation = useMutation(
    () => reconcileEmittedRetenciones(),
    { onSuccess: invalidate },
  );

  return {
    emittedRetentionsQuery,
    emitirMutation,
    reintentarMutation,
    eliminarMutation,
    reconcileMutation,
  };
};
